import { useState } from "react";
import { IonItem } from "@ionic/react";
import { StudentPolicies, SubPolicies } from "model";
import { Icon } from "swing-components";

import { translate } from "~utils";

import { EditableMarkdown, EditableText } from "../EditableFields";
import { useUpdateSchool } from "./hooks";
import { Footer, ProfileCard } from "./ProfileCard";
import { ErrorList, SchoolSitePoliciesFormSchema } from "./SchoolProfileFormUtils";

type SchoolSitePoliciesProp = {
  schoolId: string;
  subPolicies: SubPolicies;
  studentPolicies: StudentPolicies;
};

export const SchoolSitePolicies: React.FC<SchoolSitePoliciesProp> = (
  props: SchoolSitePoliciesProp,
) => {
  const { schoolId, subPolicies, studentPolicies } = props;

  const [formState, setFormState] = useState({
    subPolicies,
    studentPolicies,
  });

  const { errors, updateSchool } = useUpdateSchool({
    formState,
    validationSchema: SchoolSitePoliciesFormSchema,
    schoolId,
  });

  const onSave = async () => {
    updateSchool()
      .then(() => setEditing(false))
      .catch();
  };

  const [editing, setEditing] = useState(false);

  return (
    <ProfileCard
      header={translate("SITE_POLICIES_HEADER")}
      isEditing={editing}
      onClick={() => setEditing(true)}
    >
      <EditableText
        isInset
        lines="none"
        isEditing={editing}
        label={translate("TAKING_ATTENDANCE_LABEL")}
        value={formState?.subPolicies?.takingAttendance}
        onChange={(e) => {
          setFormState((old) => {
            return {
              ...old,
              subPolicies: {
                ...old.subPolicies,
                takingAttendance: e.detail.value,
              },
            };
          });
        }}
        placeholder={translate("TAKING_ATTENDANCE_PLACEHOLDER")}
      />
      <EditableText
        isInset
        isEditing={editing}
        label={translate("ESCALATION_LABEL")}
        value={formState?.subPolicies?.escalation}
        placeholder={translate("ESCALATION_PLACEHOLDER")}
        onChange={(e) => {
          setFormState((old) => {
            return {
              ...old,
              subPolicies: {
                ...old.subPolicies,
                escalation: e.detail.value,
              },
            };
          });
        }}
      />

      <IonItem lines="none">
        <div className="flex gap-4 items-center">
          <Icon name="Teacher" aria-label="Teacher icon" />
          <h3 className="font-medium">{translate("SUBSTITUTES_HEADER")}</h3>
        </div>
      </IonItem>
      <EditableText
        isInset
        lines="none"
        isEditing={editing}
        label={translate("LEAVING_CLASSROOM_LABEL")}
        value={formState?.subPolicies?.leavingClassroom}
        placeholder={translate("SUBS_LEAVING_CLASSROOM_PLACEHOLDER")}
        onChange={(e) => {
          setFormState((old) => {
            return {
              ...old,
              subPolicies: {
                ...old.subPolicies,
                leavingClassroom: e.detail.value,
              },
            };
          });
        }}
      />
      <EditableText
        isInset
        lines="none"
        isEditing={editing}
        label={translate("LUNCH_PROTOCOL_LABEL")}
        value={formState?.subPolicies?.lunchProtocol}
        placeholder={translate("SUBS_LUNCH_PROTOCOL_PLACEHOLDER")}
        onChange={(e) => {
          setFormState((old) => {
            return {
              ...old,
              subPolicies: {
                ...old.subPolicies,
                lunchProtocol: e.detail.value,
              },
            };
          });
        }}
      />
      <EditableText
        isInset
        lines="none"
        isEditing={editing}
        label={translate("DRESS_CODE_LABEL")}
        value={formState?.subPolicies?.dressCode}
        placeholder={translate("SUBS_DRESS_CODE_PLACEHOLDER")}
        onChange={(e) => {
          setFormState((old) => {
            return {
              ...old,
              subPolicies: {
                ...old.subPolicies,
                dressCode: e.detail.value,
              },
            };
          });
        }}
      />
      <EditableText
        isInset
        isEditing={editing}
        label={translate("ELECTRONICS_LABEL")}
        value={formState?.subPolicies?.electronics}
        placeholder={translate("SUBS_ELECTRONICS_PLACEHOLDER")}
        onChange={(e) => {
          setFormState((old) => {
            return {
              ...old,
              subPolicies: {
                ...old.subPolicies,
                electronics: e.detail.value,
              },
            };
          });
        }}
      />

      <IonItem lines="none">
        <div className="flex gap-4 items-center">
          <Icon name="Groups" />
          <h3 className="font-medium">{translate("STUDENTS_HEADER")}</h3>
        </div>
      </IonItem>

      <EditableText
        isInset
        lines="none"
        isEditing={editing}
        label={translate("LEAVING_CLASSROOM_LABEL")}
        value={formState?.studentPolicies?.leavingClassroom}
        placeholder={translate("STUDENTS_LEAVING_CLASSROOM_PLACEHOLDER")}
        onChange={(e) => {
          setFormState((old) => {
            return {
              ...old,
              studentPolicies: {
                ...old.studentPolicies,
                leavingClassroom: e.detail.value,
              },
            };
          });
        }}
      />
      <EditableText
        isInset
        lines="none"
        isEditing={editing}
        label={translate("LUNCH_PROTOCOL_LABEL")}
        value={formState?.studentPolicies?.lunchProtocol}
        placeholder={translate("STUDENTS_LUNCH_PROTOCOL_PLACEHOLDER")}
        onChange={(e) => {
          setFormState((old) => {
            return {
              ...old,
              studentPolicies: {
                ...old.studentPolicies,
                lunchProtocol: e.detail.value,
              },
            };
          });
        }}
      />
      <EditableText
        isInset
        lines="none"
        isEditing={editing}
        label={translate("DRESS_CODE_LABEL")}
        value={formState?.studentPolicies?.dressCode}
        placeholder={translate("STUDENTS_DRESS_CODE_PLACEHOLDER")}
        onChange={(e) => {
          setFormState((old) => {
            return {
              ...old,
              studentPolicies: {
                ...old.studentPolicies,
                dressCode: e.detail.value,
              },
            };
          });
        }}
      />
      <EditableText
        isInset
        isEditing={editing}
        label={translate("ELECTRONICS_LABEL")}
        value={formState?.studentPolicies?.electronics}
        placeholder={translate("STUDENTS_ELECTRONICS_PLACEHOLDER")}
        onChange={(e) => {
          setFormState((old) => {
            return {
              ...old,
              studentPolicies: {
                ...old.studentPolicies,
                electronics: e.detail.value,
              },
            };
          });
        }}
      />

      <IonItem lines="none">
        <div className="flex gap-4 items-center">
          <Icon name="Shield" aria-label="Emergency Procedures icon" />
          <h3 className="font-medium">{translate("EMERGENCY_PROCEDURES_HEADER")}</h3>
        </div>
      </IonItem>

      <EditableText
        isInset
        lines="none"
        isEditing={editing}
        label={translate("PROTOCOL_HEADER")}
        value={formState?.subPolicies?.emergencyProtocol}
        placeholder={translate("PROTOCOL_PLACEHOLDER")}
        onChange={(e) => {
          setFormState((old) => {
            return {
              ...old,
              subPolicies: {
                ...old.subPolicies,
                emergencyProtocol: e.detail.value,
              },
            };
          });
        }}
      />
      <EditableText
        isInset
        lines="none"
        isEditing={editing}
        label={translate("CODE_WORDS_HEADER")}
        value={formState?.subPolicies?.codeWords}
        placeholder={translate("CODE_WORDS_HEADER_PLACEHOLDER")}
        onChange={(e) => {
          setFormState((old) => {
            return {
              ...old,
              subPolicies: {
                ...old.subPolicies,
                codeWords: e.detail.value,
              },
            };
          });
        }}
      />
      <EditableMarkdown
        isInset
        lines="none"
        isEditing={editing}
        label={translate("LOCKDOWN_HEADER")}
        value={formState?.subPolicies?.lockdownProtocol}
        placeholder={translate("LOCKDOWN_PLACEHOLDER")}
        onChange={(e) => {
          setFormState((old) => {
            return {
              ...old,
              subPolicies: {
                ...old.subPolicies,
                lockdownProtocol: e.toString(),
              },
            };
          });
        }}
      />
      {editing && (
        <>
          {errors.length > 0 && (
            <IonItem lines="none">
              <ErrorList errorList={errors} />
            </IonItem>
          )}

          <Footer onSubmit={onSave} onCancel={() => setEditing(false)} />
        </>
      )}
    </ProfileCard>
  );
};
